import * as React from "react";
import { Button } from "@/components/ui/button";
import i18n from "@/i18n";

type Props = { children: React.ReactNode; fallback?: React.ReactNode };
type State = { error: Error | null };

export class ErrorBoundary extends React.Component<Props, State> {
  state: State = { error: null };

  static getDerivedStateFromError(error: Error): State {
    return { error };
  }

  componentDidCatch(error: Error, info: React.ErrorInfo) {
    console.error("[ErrorBoundary]", error, info.componentStack);
    const isChunkError =
      /Failed to fetch dynamically imported module|Importing a module script failed|Loading chunk \d+ failed|error loading dynamically imported module/i.test(
        error.message || "",
      );
    if (isChunkError && !sessionStorage.getItem("nextsub:chunk-reload")) {
      sessionStorage.setItem("nextsub:chunk-reload", "1");
      window.location.reload();
    }
  }

  reset = () => this.setState({ error: null });

  render() {
    if (this.state.error) {
      if (this.props.fallback) return this.props.fallback;
      const isChunkError =
        /Failed to fetch dynamically imported module|Importing a module script failed|Loading chunk \d+ failed|error loading dynamically imported module/i.test(
          this.state.error.message || "",
        );
      return (
        <div className="flex min-h-screen flex-col items-center justify-center gap-4 p-8 text-center">
          <h1 className="text-2xl font-semibold">
            {i18n.t("common.somethingWentWrong")}
          </h1>
          <p className="max-w-md text-sm text-muted-foreground">
            {isChunkError
              ? i18n.t("common.chunkLoadFailed")
              : this.state.error.message || i18n.t("common.unexpectedError")}
          </p>
          <div className="flex gap-2">
            <Button onClick={this.reset}>{i18n.t("common.tryAgain")}</Button>
            <Button
              variant="outline"
              onClick={() => {
                sessionStorage.removeItem("nextsub:chunk-reload");
                window.location.reload();
              }}
            >
              {i18n.t("common.reloadPage")}
            </Button>
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}
